import React, { useState, useEffect, useRef } from 'react';
import {
  FaFacebookF,
  FaInstagram,
  FaLinkedinIn,
  FaWhatsapp,
  FaTimes,
  FaShareAlt,
} from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';

const SocialBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const bubbleRef = useRef(null);

  // Configuración de redes sociales
  const socialLinks = [
    {
      id: 'facebook',
      name: 'Facebook',
      icon: <FaFacebookF />,
      href: 'https://www.facebook.com/unetepacksys',
      color: 'bg-[#1877F2]',
      hoverColor: 'hover:bg-[#0d65d9]',
      tooltip: 'Síguenos en Facebook',
    },
    {
      id: 'instagram',
      name: 'Instagram',
      icon: <FaInstagram />,
      href: 'https://www.instagram.com/unetepacksys/',
      color: 'bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]',
      hoverColor: 'hover:opacity-90',
      tooltip: 'Síguenos en Instagram',
    },
    {
      id: 'linkedin',
      name: 'LinkedIn',
      icon: <FaLinkedinIn />,
      href: 'https://mx.linkedin.com/company/packsys',
      color: 'bg-[#0A66C2]',
      hoverColor: 'hover:bg-[#084a8f]',
      tooltip: 'Conéctate en LinkedIn',
    },
    {
      id: 'whatsapp',
      name: 'WhatsApp',
      icon: <FaWhatsapp />,
      href: 'https://wa.me/525588693920?text=¡Hola%20me%20gustaría%20ser%20parte%20del%20equipo%20Packsys!',
      color: 'bg-[#25D366]',
      hoverColor: 'hover:bg-[#1da851]',
      tooltip: 'Contáctanos por WhatsApp',
    },
  ];

  // Cerrar al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bubbleRef.current && !bubbleRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Animaciones con Framer Motion
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.8 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        type: 'spring',
        stiffness: 260,
        damping: 20,
      },
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        type: 'spring',
        stiffness: 300,
        damping: 24,
      },
    }),
    exit: { opacity: 0, y: 20 },
  };

  return (
    <AnimatePresence>
      <motion.div
        ref={bubbleRef}
        initial={{ opacity: 0, y: 100 }}
        animate={{
          opacity: 1,
          y: 0,
          transition: { type: 'spring', stiffness: 300, damping: 30 },
        }}
        exit={{ opacity: 0, y: 100 }}
        className="fixed bottom-8 left-8 z-50"
      >
        {/* Contenedor principal de la burbuja */}
        <div className="relative">
          {/* Redes sociales expandidas */}
          {isOpen && (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className="absolute bottom-20 left-0 mb-4 flex flex-col items-end space-y-4"
            >
              {socialLinks.map((social, index) => (
                <motion.a
                  key={social.id}
                  custom={index}
                  variants={itemVariants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`group relative flex items-center justify-center w-14 h-14 rounded-full shadow-2xl transform transition-all duration-300 hover:scale-110 hover:shadow-3xl ${social.color} ${social.hoverColor}`}
                  aria-label={social.name}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                >
                  {/* Tooltip */}
                  <div className="absolute left-16 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                    <div className="bg-gray-900 text-white text-xs font-semibold px-3 py-2 rounded-lg whitespace-nowrap">
                      {social.tooltip}
                      <div className="absolute top-1/2 left-0 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-900"></div>
                    </div>
                  </div>

                  {/* Ícono */}
                  <span className="text-white text-xl">{social.icon}</span>

                  {/* Efecto de pulso */}
                  <div className="absolute inset-0 rounded-full border-2 border-white/30 animate-ping opacity-0 group-hover:opacity-100"></div>
                </motion.a>
              ))}
            </motion.div>
          )}

          {/* Botón principal de la burbuja */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={() => setIsOpen(!isOpen)}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className={`
                relative flex items-center justify-center
                w-16 h-16 rounded-full shadow-2xl
                bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500
                hover:from-indigo-600 hover:via-purple-600 hover:to-pink-600
                transition-all duration-300
                ${isOpen ? 'rotate-45' : 'rotate-0'}
                transform-gpu
                overflow-hidden
                group
              `}
            aria-label={
              isOpen ? 'Cerrar redes sociales' : 'Abrir redes sociales'
            }
          >
            {/* Fondo animado */}
            <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

            {/* Ícono principal */}
            <div className="relative z-10">
              {isOpen ? (
                <FaTimes className="text-white text-2xl transform transition-transform duration-300" />
              ) : (
                <FaShareAlt className="text-white text-2xl transform transition-transform duration-300" />
              )}
            </div>

            {/* Efecto de pulso */}
            <div
              className={`absolute inset-0 rounded-full border-4 border-white/20 ${isHovered ? 'animate-ping' : ''}`}
            ></div>
          </motion.button>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SocialBubble;
